// features/chat/pages/ChatPage.tsx
//
// Staff chat — a CS-ticket widget with privacy-scoped rooms:
//   • Floor team   → one room PER floor team; a staffer sees only the team
//                    they're assigned to (admin/Manager/HJ oversight can pick any).
//   • Reach SK/HJ  → one PRIVATE thread per requester. Requesters see only their
//                    own thread; responders (SK/HJ + oversight) get an inbox of
//                    all requesters' threads and claim/handle each.
//   • Tech Support → one global thread per requester → admins (inbox).
//
// Navigation: Home gateway → Tournament Chat / Technical Support. It embeds in
// the floating bubble or on the /chat route (push deep links ?t=&c=&r= / ?floor= / ?support=).

import { useState, useEffect, useMemo, useRef, ReactElement, ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Badge,
  Divider,
  Button,
  Chip,
  CircularProgress,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ref, onValue } from 'firebase/database';
import SendIcon from '@mui/icons-material/Send';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import GroupsIcon from '@mui/icons-material/Groups';
import GavelIcon from '@mui/icons-material/Gavel';
import EditNoteIcon from '@mui/icons-material/EditNote';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ReplayIcon from '@mui/icons-material/Replay';
import { PageContainer } from '../../../components/layout/PageContainer';
import { GlassCard } from '../../../components/common/Card';
import { db } from '../../../config/firebase';
import { useTournamentMetadata } from '../../../hooks/useTournamentMetadata';
import { UserData } from '../../../context/AuthContext';
import { CHAT_CHANNELS, ChatChannelId, ConversationMeta, CHANNEL_RESPONDER_ROLE } from '../channels';
import {
  ChatMessage,
  ChannelSummary,
  SupportRequest,
  FloorTeamInfo,
  floorBase,
  requestBase,
  supportBase,
  conversationStatus,
  isAlerting,
  sendFloorMessage,
  sendRequestMessage,
  sendSupportMessage,
  markFloorRead,
  markRequestRead,
  markSupportRead,
  subscribeFloorMessages,
  subscribeFloorSummary,
  subscribeFloorTeams,
  subscribeRequestMessages,
  subscribeRequestSummary,
  subscribeRequestInbox,
  subscribeSupportMessages,
  subscribeSupportSummary,
  subscribeOpenSupportRequests,
  subscribeConversationMeta,
  subscribeParticipants,
  claimConversation,
  joinConversation,
  releaseConversation,
  resolveConversation,
  reopenConversation,
  requestAdmin,
} from '../chatService';

function senderName(user: UserData | null): string {
  if (!user) return 'Staff';
  const full = `${user.firstName || ''} ${user.lastName || ''}`.trim();
  return full || user.displayName || user.email || 'Staff';
}

function isAdminUser(user: UserData | null): boolean {
  return !!user && (user.accountType === 'admin' || user.role === 'admin');
}

function timeLabel(ts: number): string {
  if (!ts) return '';
  return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

// Turn plain URLs (and bare www. links) in a message into clickable links.
// Splits the text on a URL pattern and renders matches as <a> that open in a
// new tab; everything else stays plain text.
const URL_RE = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;
function linkify(text: string): ReactNode {
  if (!text) return text;
  const parts = text.split(URL_RE);
  return parts.map((part, i) => {
    if (i % 2 === 1) {
      const href = part.startsWith('http') ? part : `https://${part}`;
      return (
        <a
          key={i}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#4DA3FF', wordBreak: 'break-all' }}
          onClick={(e) => e.stopPropagation()}
        >
          {part}
        </a>
      );
    }
    return part;
  });
}

const CHANNEL_META: Record<ChatChannelId, { icon: ReactElement; color: string }> = {
  floor: { icon: <GroupsIcon />, color: '#00C2A8' },
  scorekeeper: { icon: <EditNoteIcon />, color: '#3B9DFF' },
  headjudge: { icon: <GavelIcon />, color: '#FFB020' },
};
const SUPPORT_COLOR = '#FF6B6B';

// A conversation the panel can open.
type Conversation =
  | { kind: 'floor'; tid: string; tname: string; teamLetter: string }
  | { kind: 'request'; tid: string; cid: ChatChannelId; tname: string; requesterUid: string; requesterName: string }
  | { kind: 'support'; requesterUid: string; requesterName: string };

type ChatTab = 'home' | 'chats';
type HomeStage = 'gateway' | 'tournaments' | 'channels' | 'floorTeams' | 'requestInbox' | 'supportInbox';

// ── Hero ─────────────────────────────────────────────────────────────────────
function HeroHeader({ onClose }: { onClose?: () => void }): ReactElement {
  return (
    <Box
      sx={{
        position: 'relative',
        px: 2.5,
        pt: 2.5,
        pb: 3.5,
        color: '#fff',
        background: (t) =>
          `linear-gradient(180deg, ${alpha('#0A0E14', 0.3)}, ${alpha('#0A0E14', 0.55)}), ` +
          `linear-gradient(135deg, ${t.palette.primary.dark}, ${t.palette.primary.main} 62%, ${t.palette.secondary.dark})`,
      }}
    >
      {onClose && (
        <IconButton onClick={onClose} size="small" aria-label="Close chat" sx={{ position: 'absolute', top: 8, right: 8, color: alpha('#fff', 0.9) }}>
          <CloseIcon />
        </IconButton>
      )}
      <Typography sx={{ fontSize: 26, fontWeight: 800, lineHeight: 1.15, opacity: 0.92 }}>Hi there 👋</Typography>
      <Typography sx={{ fontSize: 21, fontWeight: 700, lineHeight: 1.2 }}>How can we help?</Typography>
      <Typography sx={{ fontSize: 12.5, mt: 1, color: alpha('#fff', 0.82) }}>
        Chat with your tournament team, or reach app support directly.
      </Typography>
    </Box>
  );
}

function AvatarDot({ icon, color }: { icon: ReactElement; color: string }): ReactElement {
  return (
    <Box
      sx={{
        width: 38,
        height: 38,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        color,
        backgroundColor: alpha(color, 0.16),
        '& svg': { fontSize: 21 },
      }}
    >
      {icon}
    </Box>
  );
}

function ListRow({
  icon,
  color,
  label,
  secondary,
  overline,
  ts,
  alert,
  trailing,
  first,
  onClick,
}: {
  icon: ReactElement;
  color: string;
  label: string;
  secondary: string;
  overline?: string;
  ts?: number;
  alert?: boolean;
  trailing?: ReactElement;
  first?: boolean;
  onClick: () => void;
}): ReactElement {
  return (
    <Box onClick={onClick} sx={{ ...rowSx, borderTop: first ? 'none' : '1px solid', borderColor: 'divider' }}>
      <Badge color="error" variant="dot" overlap="circular" invisible={!alert}>
        <AvatarDot icon={icon} color={color} />
      </Badge>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {overline && (
          <Typography sx={{ fontSize: '0.66rem', color: 'text.disabled', textTransform: 'uppercase', letterSpacing: 0.4, lineHeight: 1.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {overline}
          </Typography>
        )}
        <Typography sx={{ fontWeight: alert ? 800 : 600, fontSize: '0.9rem' }}>{label}</Typography>
        <Typography variant="caption" sx={{ color: alert ? 'text.primary' : 'text.secondary', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {secondary}
        </Typography>
      </Box>
      {trailing ??
        (ts ? (
          <Typography variant="caption" sx={{ color: 'text.disabled', flexShrink: 0 }}>
            {timeLabel(ts)}
          </Typography>
        ) : (
          <ChevronRightIcon sx={{ color: 'text.disabled', fontSize: 20, flexShrink: 0 }} />
        ))}
    </Box>
  );
}

function StatusChip({ meta }: { meta?: ConversationMeta }): ReactElement | null {
  const st = conversationStatus(meta);
  if (st === 'claimed') {
    return <Chip size="small" label={`Handled${meta?.claimedByName ? ` · ${meta.claimedByName}` : ''}`} sx={{ height: 20, fontSize: '0.65rem', bgcolor: alpha('#00C2A8', 0.16), color: '#00C2A8' }} />;
  }
  if (st === 'resolved') {
    return <Chip size="small" label="Resolved" sx={{ height: 20, fontSize: '0.65rem', bgcolor: alpha('#8892A0', 0.2), color: 'text.secondary' }} />;
  }
  return <Chip size="small" label="Open" sx={{ height: 20, fontSize: '0.65rem', bgcolor: alpha('#FFB020', 0.18), color: '#FFB020' }} />;
}

// ── The whole chat experience ────────────────────────────────────────────────
export function ChatPanel({
  user,
  initialConversation,
  onClose,
}: {
  user: UserData | null;
  initialConversation?: Conversation | null;
  onClose?: () => void;
}): ReactElement {
  const { tournaments, loading } = useTournamentMetadata({ subscribe: true });
  const uid = user?.uid || '';
  const admin = isAdminUser(user);
  const managerish = admin || user?.role === 'Manager' || user?.accountType === 'store';

  const [sel, setSel] = useState<Conversation | null>(initialConversation || null);
  const [tab, setTab] = useState<ChatTab>('home');
  const [stage, setStage] = useState<HomeStage>('gateway');
  const [homeTid, setHomeTid] = useState<string | null>(null);
  const [inboxCtx, setInboxCtx] = useState<{ tid: string; cid: ChatChannelId } | null>(null);

  // Tournaments this user can chat in (admin=all, store=own, staff=assigned).
  const assigned = useMemo(() => {
    const isAssigned = (t: Record<string, unknown> & { id: string }): boolean => {
      if (admin) return true;
      if (user?.tournamentAssignments?.[t.id]) return true;
      if (user?.floorTeamAssignments?.[t.id]) return true;
      if ((user as { headJudgeTeamAssignments?: Record<string, unknown> } | null)?.headJudgeTeamAssignments?.[t.id]) return true;
      if (uid && (t.staffAssignments as Record<string, unknown> | undefined)?.[uid]) return true;
      if (user?.accountType === 'store') {
        const sid = (user as { storeId?: string } | null)?.storeId;
        if (uid && t.ownerUid === uid) return true;
        if (uid && t.createdByUid === uid) return true;
        if (sid && t.ownerStoreId === sid) return true;
      }
      return false;
    };
    return (tournaments as Array<Record<string, unknown> & { id: string }>)
      .filter(isAssigned)
      .map((t) => ({ id: t.id, name: (t.name as string) || 'Tournament' }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [tournaments, user, admin, uid]);

  // Per-tournament assignment role for this user (drives responder/oversight).
  const [myRoles, setMyRoles] = useState<Record<string, string>>({});
  useEffect(() => {
    if (!uid) return;
    const unsubs = assigned.map((t) =>
      onValue(
        ref(db, `tournaments/${t.id}/staffAssignments/${uid}/role`),
        (s) => setMyRoles((p) => ({ ...p, [t.id]: (s.val() as string) || '' })),
        () => undefined,
      ),
    );
    return () => unsubs.forEach((u) => u());
  }, [assigned, uid]);

  const myRole = (tid: string): string => myRoles[tid] || '';
  const myFloorLetter = (tid: string): string => user?.floorTeamAssignments?.[tid] || '';
  const isResponder = (tid: string, cid: ChatChannelId): boolean => {
    if (managerish) return true;
    const target = CHANNEL_RESPONDER_ROLE[cid];
    if (target && user?.role === target) return true;
    if (target && myRole(tid) === target) return true;
    if (cid === 'headjudge' && user?.headJudgeTeamAssignments?.[tid]) return true;
    return false;
  };
  const isFloorOversight = (tid: string): boolean =>
    managerish || user?.role === 'Head Judge' || myRole(tid) === 'Head Judge' || myRole(tid) === 'Manager' || !!user?.headJudgeTeamAssignments?.[tid];

  // Live summaries for the picker badges + Your Chats.
  const [floorSummaries, setFloorSummaries] = useState<Record<string, ChannelSummary>>({});
  const [reqInbox, setReqInbox] = useState<Record<string, Partial<Record<ChatChannelId, SupportRequest[]>>>>({});
  const [reqOwn, setReqOwn] = useState<Record<string, Partial<Record<ChatChannelId, ChannelSummary>>>>({});
  const [supportSummary, setSupportSummary] = useState<ChannelSummary | null>(null);
  const [supportRequests, setSupportRequests] = useState<SupportRequest[]>([]);

  useEffect(() => {
    if (!uid) return;
    const unsubs: Array<() => void> = [];
    for (const t of assigned) {
      const letter = myFloorLetter(t.id);
      if (letter) {
        unsubs.push(subscribeFloorSummary(t.id, letter, uid, (s) => setFloorSummaries((p) => ({ ...p, [t.id]: s }))));
      }
      for (const cid of ['scorekeeper', 'headjudge'] as ChatChannelId[]) {
        if (isResponder(t.id, cid)) {
          unsubs.push(subscribeRequestInbox(t.id, cid, uid, (list) => setReqInbox((p) => ({ ...p, [t.id]: { ...(p[t.id] || {}), [cid]: list } }))));
        } else {
          unsubs.push(subscribeRequestSummary(t.id, cid, uid, uid, (s) => setReqOwn((p) => ({ ...p, [t.id]: { ...(p[t.id] || {}), [cid]: s } }))));
        }
      }
    }
    return () => unsubs.forEach((u) => u());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assigned, uid, myRoles, admin, managerish, user?.floorTeamAssignments, user?.role, user?.headJudgeTeamAssignments]);

  useEffect(() => {
    if (!uid) return;
    if (admin) return subscribeOpenSupportRequests(uid, setSupportRequests);
    return subscribeSupportSummary(uid, uid, setSupportSummary);
  }, [uid, admin]);

  const channelAlert = (tid: string, cid: ChatChannelId): boolean => {
    if (cid === 'floor') {
      const letter = myFloorLetter(tid);
      return !!(letter && floorSummaries[tid]?.unread);
    }
    if (isResponder(tid, cid)) {
      return (reqInbox[tid]?.[cid] || []).some((r) => isAlerting({ unread: r.unread, meta: r.meta }, true, uid));
    }
    const s = reqOwn[tid]?.[cid];
    return !!s && isAlerting(s, false, uid);
  };
  const channelVisible = (tid: string, cid: ChatChannelId): boolean =>
    cid === 'floor' ? !!myFloorLetter(tid) || isFloorOversight(tid) : true;
  const tournamentAlert = (tid: string): boolean =>
    CHAT_CHANNELS.some((ch) => channelVisible(tid, ch.id) && channelAlert(tid, ch.id));

  const supportAlert = admin
    ? supportRequests.some((r) => isAlerting({ unread: r.unread, meta: r.meta }, true, uid))
    : !!supportSummary && isAlerting(supportSummary, false, uid);

  // Flattened Your-Chats rows.
  type Row = { key: string; conv: Conversation; overline?: string; label: string; secondary: string; ts?: number; alert: boolean; meta?: ConversationMeta; icon: ReactElement; color: string };
  const ongoing: Row[] = useMemo(() => {
    const rows: Row[] = [];
    for (const t of assigned) {
      const letter = myFloorLetter(t.id);
      const fs = letter ? floorSummaries[t.id] : undefined;
      if (letter && fs?.lastTs) {
        rows.push({
          key: `floor-${t.id}`,
          conv: { kind: 'floor', tid: t.id, tname: t.name, teamLetter: letter },
          overline: t.name,
          label: `Floor team ${letter}`,
          secondary: fs.lastText ? `${fs.lastName}: ${fs.lastText}` : 'Floor team chat',
          ts: fs.lastTs,
          alert: !!fs.unread,
          icon: CHANNEL_META.floor.icon,
          color: CHANNEL_META.floor.color,
        });
      }
      for (const cid of ['scorekeeper', 'headjudge'] as ChatChannelId[]) {
        const label = CHAT_CHANNELS.find((c) => c.id === cid)?.label || 'Chat';
        if (isResponder(t.id, cid)) {
          for (const r of reqInbox[t.id]?.[cid] || []) {
            if (r.unread || r.meta?.claimedBy === uid) {
              rows.push({
                key: `req-${t.id}-${cid}-${r.requesterUid}`,
                conv: { kind: 'request', tid: t.id, cid, tname: t.name, requesterUid: r.requesterUid, requesterName: r.requesterName },
                overline: `${t.name} · ${label}`,
                label: r.requesterName,
                secondary: r.lastText ? `${r.lastName}: ${r.lastText}` : 'Request',
                ts: r.lastTs,
                alert: isAlerting({ unread: r.unread, meta: r.meta }, true, uid),
                meta: r.meta,
                icon: CHANNEL_META[cid].icon,
                color: CHANNEL_META[cid].color,
              });
            }
          }
        } else {
          const s = reqOwn[t.id]?.[cid];
          if (s?.lastTs) {
            rows.push({
              key: `reqown-${t.id}-${cid}`,
              conv: { kind: 'request', tid: t.id, cid, tname: t.name, requesterUid: uid, requesterName: senderName(user) },
              overline: `${t.name} · ${label}`,
              label,
              secondary: s.lastText ? `${s.lastName}: ${s.lastText}` : 'Your request',
              ts: s.lastTs,
              alert: !!s.unread,
              meta: s.meta,
              icon: CHANNEL_META[cid].icon,
              color: CHANNEL_META[cid].color,
            });
          }
        }
      }
    }
    return rows.sort((a, b) => (b.ts || 0) - (a.ts || 0));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assigned, floorSummaries, reqInbox, reqOwn, myRoles, uid, user]);

  const hasUnreadChats = ongoing.some((r) => r.alert) || supportAlert;

  const openChannel = (tid: string, tname: string, cid: ChatChannelId) => {
    if (cid === 'floor') {
      const letter = myFloorLetter(tid);
      if (letter) setSel({ kind: 'floor', tid, tname, teamLetter: letter });
      else setStage('floorTeams');
      return;
    }
    if (isResponder(tid, cid)) {
      setInboxCtx({ tid, cid });
      setStage('requestInbox');
    } else {
      setSel({ kind: 'request', tid, cid, tname, requesterUid: uid, requesterName: senderName(user) });
    }
  };

  const openSupport = () => {
    if (admin) {
      setTab('home');
      setStage('supportInbox');
    } else {
      setSel({ kind: 'support', requesterUid: uid, requesterName: senderName(user) });
    }
  };

  // Thread view takes over the whole panel.
  if (sel) {
    const responder =
      sel.kind === 'request' ? isResponder(sel.tid, sel.cid) : sel.kind === 'support' ? admin : false;
    return <ConversationThread conversation={sel} user={user} admin={admin} responder={responder} onBack={() => setSel(null)} onClose={onClose} />;
  }

  const homeTournament = homeTid ? assigned.find((t) => t.id === homeTid) : null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, backgroundColor: 'background.default' }}>
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {/* HOME · GATEWAY */}
        {tab === 'home' && stage === 'gateway' && (
          <>
            <HeroHeader onClose={onClose} />
            <Box sx={{ px: 1.5, pb: 1.5, mt: 1.5 }}>
              <Box sx={{ ...cardSx, overflow: 'hidden' }}>
                <ListRow
                  first
                  icon={<ChatBubbleOutlineIcon />}
                  color="#00C2A8"
                  label="Tournament Chat"
                  secondary="Your floor team, or reach a scorekeeper / head judge"
                  alert={assigned.some((t) => tournamentAlert(t.id))}
                  onClick={() => setStage('tournaments')}
                />
                <ListRow
                  icon={<SupportAgentIcon />}
                  color={SUPPORT_COLOR}
                  label="Technical Support"
                  secondary={admin ? 'View incoming support requests' : 'Chat directly with the app admins'}
                  alert={supportAlert}
                  onClick={openSupport}
                />
              </Box>
            </Box>
          </>
        )}

        {/* HOME · TOURNAMENT PICKER */}
        {tab === 'home' && stage === 'tournaments' && (
          <>
            <PanelHeader title="Tournament Chat" subtitle="Which tournament?" onBack={() => setStage('gateway')} onClose={onClose} />
            <Box sx={{ px: 1.5, py: 1.5 }}>
              {loading && assigned.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
              ) : assigned.length === 0 ? (
                <Box sx={{ ...cardSx, p: 3, textAlign: 'center' }}>
                  <Typography sx={{ color: 'text.secondary' }}>No assigned tournaments yet.</Typography>
                </Box>
              ) : (
                <Box sx={{ ...cardSx, overflow: 'hidden' }}>
                  {assigned.map((t, i) => (
                    <ListRow
                      key={t.id}
                      first={i === 0}
                      icon={<EmojiEventsIcon />}
                      color="#7C9CFF"
                      label={t.name}
                      secondary="Choose who to reach"
                      alert={tournamentAlert(t.id)}
                      onClick={() => { setHomeTid(t.id); setStage('channels'); }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          </>
        )}

        {/* HOME · CHANNEL PICKER */}
        {tab === 'home' && stage === 'channels' && homeTournament && (
          <>
            <PanelHeader title={homeTournament.name} subtitle="Who do you want to reach?" onBack={() => setStage('tournaments')} onClose={onClose} />
            <Box sx={{ px: 1.5, py: 1.5 }}>
              <Box sx={{ ...cardSx, overflow: 'hidden' }}>
                {CHAT_CHANNELS.filter((ch) => channelVisible(homeTournament.id, ch.id)).map((ch, i) => {
                  const isResp = ch.id !== 'floor' && isResponder(homeTournament.id, ch.id);
                  const secondary =
                    ch.id === 'floor'
                      ? myFloorLetter(homeTournament.id)
                        ? `Your team (${myFloorLetter(homeTournament.id)})`
                        : 'Pick a floor team'
                      : isResp
                        ? 'Incoming requests'
                        : ch.hint;
                  return (
                    <ListRow
                      key={ch.id}
                      first={i === 0}
                      icon={CHANNEL_META[ch.id].icon}
                      color={CHANNEL_META[ch.id].color}
                      label={ch.label}
                      secondary={secondary}
                      alert={channelAlert(homeTournament.id, ch.id)}
                      onClick={() => openChannel(homeTournament.id, homeTournament.name, ch.id)}
                    />
                  );
                })}
              </Box>
            </Box>
          </>
        )}

        {/* HOME · FLOOR TEAM PICKER (oversight) */}
        {tab === 'home' && stage === 'floorTeams' && homeTournament && (
          <FloorTeamPicker
            tid={homeTournament.id}
            tname={homeTournament.name}
            onBack={() => setStage('channels')}
            onClose={onClose}
            onPick={(letter) => setSel({ kind: 'floor', tid: homeTournament.id, tname: homeTournament.name, teamLetter: letter })}
          />
        )}

        {/* HOME · REQUEST INBOX (responder) */}
        {tab === 'home' && stage === 'requestInbox' && inboxCtx && (
          <>
            <PanelHeader
              title={CHAT_CHANNELS.find((c) => c.id === inboxCtx.cid)?.label || 'Requests'}
              subtitle={assigned.find((t) => t.id === inboxCtx.tid)?.name}
              onBack={() => setStage('channels')}
              onClose={onClose}
            />
            <Box sx={{ px: 1.5, py: 1.5 }}>
              {(reqInbox[inboxCtx.tid]?.[inboxCtx.cid] || []).length === 0 ? (
                <Box sx={{ ...cardSx, p: 3, textAlign: 'center' }}>
                  <Typography sx={{ color: 'text.secondary' }}>No requests right now.</Typography>
                </Box>
              ) : (
                <Box sx={{ ...cardSx, overflow: 'hidden' }}>
                  {(reqInbox[inboxCtx.tid]?.[inboxCtx.cid] || []).map((r, i) => (
                    <ListRow
                      key={r.requesterUid}
                      first={i === 0}
                      icon={CHANNEL_META[inboxCtx.cid].icon}
                      color={CHANNEL_META[inboxCtx.cid].color}
                      label={r.requesterName}
                      secondary={r.lastText ? `${r.lastName}: ${r.lastText}` : 'Request'}
                      ts={r.lastTs}
                      alert={isAlerting({ unread: r.unread, meta: r.meta }, true, uid)}
                      trailing={<StatusChip meta={r.meta} />}
                      onClick={() => setSel({ kind: 'request', tid: inboxCtx.tid, cid: inboxCtx.cid, tname: assigned.find((t) => t.id === inboxCtx.tid)?.name || 'Tournament', requesterUid: r.requesterUid, requesterName: r.requesterName })}
                    />
                  ))}
                </Box>
              )}
            </Box>
          </>
        )}

        {/* HOME · SUPPORT INBOX (admins) */}
        {tab === 'home' && stage === 'supportInbox' && (
          <>
            <PanelHeader title="Technical Support" subtitle="Incoming requests" onBack={() => setStage('gateway')} onClose={onClose} />
            <Box sx={{ px: 1.5, py: 1.5 }}>
              {supportRequests.length === 0 ? (
                <Box sx={{ ...cardSx, p: 3, textAlign: 'center' }}>
                  <Typography sx={{ color: 'text.secondary' }}>No support requests right now.</Typography>
                </Box>
              ) : (
                <Box sx={{ ...cardSx, overflow: 'hidden' }}>
                  {supportRequests.map((r, i) => (
                    <ListRow
                      key={r.requesterUid}
                      first={i === 0}
                      icon={<SupportAgentIcon />}
                      color={SUPPORT_COLOR}
                      label={r.requesterName}
                      secondary={r.lastText ? `${r.lastName}: ${r.lastText}` : 'Support request'}
                      ts={r.lastTs}
                      alert={isAlerting({ unread: r.unread, meta: r.meta }, true, uid)}
                      trailing={<StatusChip meta={r.meta} />}
                      onClick={() => setSel({ kind: 'support', requesterUid: r.requesterUid, requesterName: r.requesterName })}
                    />
                  ))}
                </Box>
              )}
            </Box>
          </>
        )}

        {/* YOUR CHATS */}
        {tab === 'chats' && (
          <>
            <PanelHeader title="Your chats" subtitle="Ongoing conversations" onClose={onClose} />
            <Box sx={{ px: 1.5, py: 1.5 }}>
              {ongoing.length === 0 && !(supportSummary?.lastTs) && (!admin || supportRequests.length === 0) ? (
                <Box sx={{ ...cardSx, p: 3, textAlign: 'center' }}>
                  <Typography sx={{ color: 'text.secondary' }}>No conversations yet. Start one from Home.</Typography>
                </Box>
              ) : (
                <Box sx={{ ...cardSx, overflow: 'hidden' }}>
                  {!admin && supportSummary?.lastTs && (
                    <ListRow
                      first
                      icon={<SupportAgentIcon />}
                      color={SUPPORT_COLOR}
                      overline="Technical Support"
                      label="App admins"
                      secondary={supportSummary.lastText ? `${supportSummary.lastName}: ${supportSummary.lastText}` : 'Support'}
                      ts={supportSummary.lastTs}
                      alert={isAlerting(supportSummary, false, uid)}
                      onClick={() => setSel({ kind: 'support', requesterUid: uid, requesterName: senderName(user) })}
                    />
                  )}
                  {admin &&
                    supportRequests
                      .filter((r) => r.unread || r.meta?.claimedBy === uid)
                      .map((r, i) => (
                        <ListRow
                          key={`sup-${r.requesterUid}`}
                          first={i === 0}
                          icon={<SupportAgentIcon />}
                          color={SUPPORT_COLOR}
                          overline="Technical Support"
                          label={r.requesterName}
                          secondary={r.lastText ? `${r.lastName}: ${r.lastText}` : 'Support request'}
                          ts={r.lastTs}
                          alert={isAlerting({ unread: r.unread, meta: r.meta }, true, uid)}
                          trailing={<StatusChip meta={r.meta} />}
                          onClick={() => setSel({ kind: 'support', requesterUid: r.requesterUid, requesterName: r.requesterName })}
                        />
                      ))}
                  {ongoing.map((r, i) => (
                    <ListRow
                      key={r.key}
                      first={i === 0 && !admin && !supportSummary?.lastTs}
                      icon={r.icon}
                      color={r.color}
                      overline={r.overline}
                      label={r.label}
                      secondary={r.secondary}
                      ts={r.ts}
                      alert={r.alert}
                      trailing={r.meta && conversationStatus(r.meta) !== 'open' ? <StatusChip meta={r.meta} /> : undefined}
                      onClick={() => setSel(r.conv)}
                    />
                  ))}
                </Box>
              )}
            </Box>
          </>
        )}
      </Box>

      {/* Bottom sub-tabs */}
      <Box sx={{ display: 'flex', flexShrink: 0, borderTop: '1px solid', borderColor: 'divider', backgroundColor: 'background.paper' }}>
        {([
          { key: 'home' as ChatTab, label: 'Home', icon: <HomeOutlinedIcon />, badge: false },
          { key: 'chats' as ChatTab, label: 'Your Chats', icon: <ForumOutlinedIcon />, badge: hasUnreadChats },
        ]).map((item) => {
          const active = tab === item.key;
          return (
            <Box
              key={item.key}
              role="button"
              onClick={() => { setTab(item.key); if (item.key === 'home') { setStage('gateway'); setHomeTid(null); } }}
              sx={{ flex: 1, py: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25, cursor: 'pointer', color: active ? 'primary.main' : 'text.secondary', '& svg': { fontSize: 22 } }}
            >
              <Badge color="error" variant="dot" invisible={!item.badge}>{item.icon}</Badge>
              <Typography sx={{ fontSize: 11, fontWeight: active ? 700 : 500 }}>{item.label}</Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

// Floor-team picker for oversight roles.
function FloorTeamPicker({ tid, tname, onBack, onClose, onPick }: { tid: string; tname: string; onBack: () => void; onClose?: () => void; onPick: (letter: string) => void }): ReactElement {
  const [teams, setTeams] = useState<FloorTeamInfo[] | null>(null);
  useEffect(() => subscribeFloorTeams(tid, setTeams), [tid]);
  return (
    <>
      <PanelHeader title="Floor teams" subtitle={tname} onBack={onBack} onClose={onClose} />
      <Box sx={{ px: 1.5, py: 1.5 }}>
        {teams === null ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={22} /></Box>
        ) : teams.length === 0 ? (
          <Box sx={{ ...cardSx, p: 3, textAlign: 'center' }}>
            <Typography sx={{ color: 'text.secondary' }}>No floor teams yet.</Typography>
          </Box>
        ) : (
          <Box sx={{ ...cardSx, overflow: 'hidden' }}>
            {teams.map((t, i) => (
              <ListRow key={t.letter} first={i === 0} icon={<GroupsIcon />} color={CHANNEL_META.floor.color} label={t.label} secondary="Open team chat" onClick={() => onPick(t.letter)} />
            ))}
          </Box>
        )}
      </Box>
    </>
  );
}

const cardSx = {
  borderRadius: 3,
  backgroundColor: 'background.paper',
  border: '1px solid',
  borderColor: 'divider',
  boxShadow: '0 6px 20px rgba(0,0,0,0.18)',
} as const;

const rowSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  p: 1.5,
  cursor: 'pointer',
  transition: 'background-color .12s',
  '&:hover': { backgroundColor: alpha('#FFFFFF', 0.04) },
} as const;

function PanelHeader({ title, onBack, subtitle, onClose, actions }: { title: string; subtitle?: string; onBack?: () => void; onClose?: () => void; actions?: ReactElement }): ReactElement {
  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 1 }}>
        {onBack && (
          <IconButton onClick={onBack} size="small"><ArrowBackIcon /></IconButton>
        )}
        <Box sx={{ flex: 1, minWidth: 0, pl: onBack ? 0 : 0.5 }}>
          <Typography sx={{ fontWeight: 700 }} noWrap>{title}</Typography>
          {subtitle && <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap component="div">{subtitle}</Typography>}
        </Box>
        {actions}
        {onClose && <IconButton onClick={onClose} size="small" aria-label="Close chat"><CloseIcon /></IconButton>}
      </Box>
      <Divider />
    </>
  );
}

// ── Unified thread (floor team / reach-a-role request / global support) ───────
function ConversationThread({ conversation, user, admin, responder, onBack, onClose }: { conversation: Conversation; user: UserData | null; admin: boolean; responder: boolean; onBack: () => void; onClose?: () => void }): ReactElement {
  const kind = conversation.kind;
  const tid = kind !== 'support' ? conversation.tid : '';
  const cid = kind === 'request' ? conversation.cid : undefined;
  const teamLetter = kind === 'floor' ? conversation.teamLetter : '';
  const requesterUid = kind === 'support' || kind === 'request' ? conversation.requesterUid : '';
  const basePath =
    kind === 'floor'
      ? floorBase(tid, teamLetter)
      : kind === 'request'
        ? requestBase(tid, cid as ChatChannelId, requesterUid)
        : supportBase(requesterUid);

  const title =
    kind === 'floor'
      ? `Floor team ${teamLetter}`
      : kind === 'support'
        ? 'Technical Support'
        : CHAT_CHANNELS.find((c) => c.id === cid)?.label || 'Chat';
  const subtitle =
    kind === 'support'
      ? conversation.requesterName ? `with ${conversation.requesterName}` : 'App admins'
      : kind === 'request'
        ? responder ? `with ${conversation.requesterName}` : conversation.tname
        : conversation.tname;

  const isRequest = kind === 'request' || kind === 'support'; // has claim lifecycle
  const isRequestKind = kind === 'request';

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [meta, setMeta] = useState<ConversationMeta>({});
  const [participants, setParticipants] = useState<Record<string, boolean>>({});
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [busy, setBusy] = useState(false);
  const endRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const unsubM =
      kind === 'floor'
        ? subscribeFloorMessages(tid, teamLetter, setMessages)
        : kind === 'request'
          ? subscribeRequestMessages(tid, cid as ChatChannelId, requesterUid, setMessages)
          : subscribeSupportMessages(requesterUid, setMessages);
    const unsubMeta = subscribeConversationMeta(basePath, setMeta);
    const unsubP = subscribeParticipants(basePath, setParticipants);
    return () => { unsubM(); unsubMeta(); unsubP(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basePath]);

  useEffect(() => {
    if (!user?.uid) return;
    if (kind === 'floor') markFloorRead(user.uid, tid, teamLetter).catch(() => undefined);
    else if (kind === 'request') markRequestRead(user.uid, tid, cid as ChatChannelId, requesterUid).catch(() => undefined);
    else markSupportRead(user.uid, requesterUid).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, basePath, messages.length]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages.length]);

  const uid = user?.uid || '';
  const status = conversationStatus(meta);
  const sender = { uid, name: senderName(user), role: user?.role };

  const isResponder = kind === 'floor' ? false : kind === 'support' ? admin : responder;
  const isRequester = kind === 'floor' ? true : uid === requesterUid;
  const claimedByMe = !!uid && meta.claimedBy === uid;
  const joined = isRequester || claimedByMe || !!participants[uid];
  const canCompose = !isRequest || joined;
  const canManage = claimedByMe || admin || (isRequestKind && responder);

  const doSend = async () => {
    if (!draft.trim() || !uid || sending) return;
    setSending(true);
    try {
      if (kind === 'floor') await sendFloorMessage(tid, teamLetter, draft, sender);
      else if (kind === 'request') await sendRequestMessage(tid, cid as ChatChannelId, requesterUid, draft, sender);
      else await sendSupportMessage(requesterUid, draft, sender);
      setDraft('');
    } finally {
      setSending(false);
    }
  };

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    try { await fn(); } finally { setBusy(false); }
  };
  const doJoin = () => run(() => (status === 'open' ? claimConversation(basePath, sender) : joinConversation(basePath, uid)));

  const headerActions = (
    <>
      {isRequestKind && !meta.adminRequested && canCompose && (
        <IconButton size="small" aria-label="Add an admin" disabled={busy} onClick={() => run(() => requestAdmin(basePath))} title="Add an admin">
          <AdminPanelSettingsIcon sx={{ fontSize: 19 }} />
        </IconButton>
      )}
      {isRequest && canManage && status !== 'resolved' && (
        <IconButton size="small" aria-label="Mark resolved" disabled={busy} onClick={() => run(() => resolveConversation(basePath))} title="Mark resolved">
          <CheckCircleOutlineIcon sx={{ fontSize: 19 }} />
        </IconButton>
      )}
    </>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <PanelHeader title={title} subtitle={subtitle} onBack={onBack} onClose={onClose} actions={headerActions} />

      {isRequest && (status !== 'open' || meta.adminRequested) && (
        <Box sx={{ px: 1.5, py: 0.75, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', borderBottom: '1px solid', borderColor: 'divider', backgroundColor: alpha('#FFFFFF', 0.03) }}>
          {status === 'claimed' && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              ✓ Handled by <strong>{claimedByMe ? 'you' : meta.claimedByName || 'a teammate'}</strong>
            </Typography>
          )}
          {status === 'resolved' && <Typography variant="caption" sx={{ color: 'text.secondary' }}>This request was resolved.</Typography>}
          {meta.adminRequested && status !== 'resolved' && (
            <Chip size="small" label="Admin pulled in" sx={{ height: 20, fontSize: '0.62rem', bgcolor: alpha(SUPPORT_COLOR, 0.16), color: SUPPORT_COLOR }} />
          )}
          <Box sx={{ flex: 1 }} />
          {status === 'claimed' && canManage && (
            <Button size="small" disabled={busy} onClick={() => run(() => releaseConversation(basePath))} sx={{ minWidth: 0, fontSize: '0.7rem' }} startIcon={<ReplayIcon sx={{ fontSize: 15 }} />}>Release</Button>
          )}
          {status === 'resolved' && isResponder && (
            <Button size="small" disabled={busy} onClick={() => run(() => reopenConversation(basePath))} sx={{ minWidth: 0, fontSize: '0.7rem' }} startIcon={<ReplayIcon sx={{ fontSize: 15 }} />}>Reopen</Button>
          )}
        </Box>
      )}

      <Box sx={{ flex: 1, overflowY: 'auto', px: 1.5, py: 1 }}>
        {messages.length === 0 ? (
          <Typography sx={{ color: 'text.secondary', textAlign: 'center', py: 4 }}>
            {isResponder && isRequest ? 'No messages yet.' : 'No messages yet — say hello 👋'}
          </Typography>
        ) : (
          messages.map((m) => {
            const mine = m.uid === uid;
            return (
              <Box key={m.id} sx={{ display: 'flex', flexDirection: 'column', alignItems: mine ? 'flex-end' : 'flex-start', mb: 1 }}>
                {!mine && (
                  <Typography variant="caption" sx={{ color: 'text.secondary', ml: 1 }}>
                    {m.name}{m.role ? ` · ${m.role}` : ''}
                  </Typography>
                )}
                <Box
                  sx={{
                    maxWidth: '85%',
                    px: 1.5,
                    py: 1,
                    borderRadius: 3,
                    backgroundColor: (t) => (mine ? alpha(t.palette.primary.main, 0.25) : alpha('#FFFFFF', 0.06)),
                    border: '1px solid',
                    borderColor: (t) => (mine ? alpha(t.palette.primary.main, 0.4) : alpha('#FFFFFF', 0.08)),
                  }}
                >
                  <Typography sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.88rem' }}>{linkify(m.text)}</Typography>
                </Box>
                <Typography variant="caption" sx={{ color: 'text.disabled', mx: 1 }}>{timeLabel(m.ts)}</Typography>
              </Box>
            );
          })
        )}
        <div ref={endRef} />
      </Box>

      {canCompose ? (
        <Box sx={{ display: 'flex', gap: 1, p: 1, borderTop: '1px solid', borderColor: 'divider' }}>
          <TextField
            fullWidth
            size="small"
            placeholder={`Message ${title}…`}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); doSend(); } }}
            multiline
            maxRows={4}
          />
          <IconButton color="primary" onClick={doSend} disabled={!draft.trim() || sending}><SendIcon /></IconButton>
        </Box>
      ) : (
        <Box sx={{ p: 1.25, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button fullWidth variant="contained" disabled={busy} onClick={doJoin}>
            {status === 'open' ? 'Join & respond' : 'Join this chat'}
          </Button>
          {status === 'claimed' && !claimedByMe && (
            <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: 'text.disabled', mt: 0.5 }}>
              Already handled by {meta.claimedByName || 'a teammate'} — join only if you need to step in.
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}

// ── /chat route (push deep links) ────────────────────────────────────────────
export function ChatPage({ user }: { user: UserData | null }): ReactElement {
  const [params] = useSearchParams();
  const support = params.get('support');
  const t = params.get('t');
  const c = params.get('c') as ChatChannelId | null;
  const r = params.get('r');
  const floor = params.get('floor');

  let initial: Conversation | null = null;
  if (support) {
    initial = { kind: 'support', requesterUid: support, requesterName: '' };
  } else if (t && floor) {
    initial = { kind: 'floor', tid: t, tname: 'Tournament', teamLetter: floor };
  } else if (t && c && r && CHAT_CHANNELS.some((ch) => ch.id === c)) {
    initial = { kind: 'request', tid: t, cid: c, tname: 'Tournament', requesterUid: r, requesterName: '' };
  }

  return (
    <PageContainer user={user} title="Staff Chat" titleGradient>
      <Box sx={{ maxWidth: 720, mx: 'auto', height: 'calc(100vh - 220px)' }}>
        <GlassCard sx={{ height: '100%' }}>
          <ChatPanel user={user} initialConversation={initial} />
        </GlassCard>
      </Box>
    </PageContainer>
  );
}

export default ChatPage;
